import {
  AttributeDictionaryValue,
  Layer,
  LayerAttributeDictionary,
  LayerAttributeDictionaryValue,
  LayerAttributeNumber,
  Obj
} from '../../../services/models'

// Фильтры в том виде, в котором они хранятся в группе:
//   d:<id словаря>  -> [valueId, ...]
//   n:<id числа>    -> [min, max], где null означает «граница не задана»
// null, а не 0, важен: ноль — допустимое значение координаты, и подменять
// им незаданную границу значило бы получить неверный результат.
export type NumberRange = [number | null, number | null]

// Словарный фильтр — список valueId, числовой — пара границ.
export type GroupFilters = { [key: string]: number[] | NumberRange }

// Сырые значения формы. Числовые атрибуты дают два отдельных поля с суффиксами
// _min/_max, словарные — готовый массив выбранных чекбоксов.
export type RawGroupForm = { [key: string]: unknown }

export const NUM_MIN = '_min'
export const NUM_MAX = '_max'

const asNumber = (value: unknown): number | null =>
  (typeof value === 'number' && !Number.isNaN(value)) ? value : null

export const asFormNumber = asNumber

// Числовой фильтр задан всегда — хотя бы одной границей, например «до 500».
export const isNumberFilterSet = (filters: GroupFilters, attrId: number) => {
  const range = filters[`n:${attrId}`] as NumberRange | undefined
  return range !== undefined && (range[0] !== null || range[1] !== null)
}

// Значения формы -> хранимые фильтры. Незаданные границы отбрасываются по
// отдельности, поэтому «заполнен только max» становится «меньше или равно».
export const toFilters = (raw: RawGroupForm): GroupFilters => {
  const filters: GroupFilters = {}
  Object.keys(raw).forEach(key => {
    if (key.startsWith('d:')) {
      const values = raw[key]
      if (Array.isArray(values) && values.length > 0) filters[key] = values as number[]
      return
    }
    if (key.startsWith('n:') && key.endsWith(NUM_MIN)) {
      const attrId = key.slice('n:'.length, key.length - NUM_MIN.length)
      const min = asNumber(raw[key])
      const max = asNumber(raw[`n:${attrId}${NUM_MAX}`])
      if (min !== null || max !== null) filters[`n:${attrId}`] = [min, max]
    }
  })
  return filters
}

// Хранимые фильтры -> значения формы, для initialValues при повторном
// открытии панели. Ключи _min/_max нужны форме, а n:<id> — бэкенду.
export const toInitialForm = (filters: GroupFilters): RawGroupForm => {
  const raw: RawGroupForm = {}
  Object.keys(filters).forEach(key => {
    if (key.startsWith('n:')) {
      const [min, max] = filters[key] as NumberRange
      raw[`n:${key.slice(2)}${NUM_MIN}`] = min ?? undefined
      raw[`n:${key.slice(2)}${NUM_MAX}`] = max ?? undefined
      return
    }
    raw[key] = filters[key]
  })
  return raw
}

// Человекочитаемая подпись атрибута с единицей измерения.
export const attributeLabel = (attr: LayerAttributeNumber) =>
  attr.unit ? `${attr.name} (${attr.unit})` : attr.name

// Подпись диапазона для сводки: «от 100», «до 500», «100 – 500».
export const rangeLabel = (filters: GroupFilters, attrId: number) => {
  if (!isNumberFilterSet(filters, attrId)) return ''
  const [min, max] = filters[`n:${attrId}`] as NumberRange
  if (min !== null && max !== null) return `${formatBoundary(min)} – ${formatBoundary(max)}`
  if (min !== null) return `от ${formatBoundary(min)}`
  if (max !== null) return `до ${formatBoundary(max)}`
  return ''
}

export type LayerGroup = {
  // Стабильный идентификатор группы: используется как React-ключ и как ключ
  // в кэшах, поэтому не должен переиспользоваться после удаления группы.
  key: string,
  layerId: number,
  color: string,
  // Фильтры именно в применённом виде — по ним рисуется сводка в свёрнутом
  // состоянии панели и переинициализируется форма при повторном открытии.
  filters: GroupFilters,
  items: Obj[],
  loading: boolean,
  applied: boolean
}

// Состояние блоков «показать всё» и поиска по значениям словарей. Ключуется по
// id словаря и не зависит от группы, поэтому общее на все группы.
export type DictionaryUi = {
  values: { [k: number]: AttributeDictionaryValue[] },
  showMore: { [k: number]: boolean },
  search: { [k: number]: string },
  setSearch: (id: number, value: string) => void,
  toggleShowMore: (id: number) => void,
  // Значения для чекбоксов с учётом режима «показать всё» и строки поиска.
  optionsFor: (ad: LayerAttributeDictionary) => LayerAttributeDictionaryValue[]
}

// Сколько фильтров реально ограничивают выбор. Пустые группы чекбоксов (сняли
// всё) не считаются, как и диапазон, у которого не задана ни одна граница.
export const appliedFilterCount = (filters: GroupFilters) =>
  Object.keys(filters).filter(key => {
    const value = filters[key]
    if (!Array.isArray(value)) return false
    if (key.startsWith('n:')) {
      const [min, max] = value as NumberRange
      return min !== null || max !== null
    }
    return value.length > 0
  }).length

// Палитра выдаётся по порядку добавления слоёв и переиспользуется по кругу.
export const GROUP_COLORS = [
  '#2f6fd0',
  '#d9453d',
  '#2f9e44',
  '#e8830c',
  '#8b46c9',
  '#0e9aa7',
  '#c2255c',
  '#5c6b73'
]

export const pickGroupColor = (index: number) => GROUP_COLORS[index % GROUP_COLORS.length]

// Шаг ползунка подбирается под ширину диапазона. Нужен потому, что шкала
// линейная и может быть очень узкой (1900–2018) или очень широкой (1–8865),
// и шаг 1 в первом случае не даст задать дробное значение вроде 8.25.
const NICE_STEPS = [0.01, 0.05, 0.1, 0.5, 1, 2, 5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000]

export const sliderStep = (min: number, max: number) => {
  const span = Math.abs(max - min)
  return NICE_STEPS.find(step => span / step <= 200) ?? NICE_STEPS[NICE_STEPS.length - 1]
}

// Значение вне [min, max] приведёт к пустой выдаче, поэтому такие границы
// отклоняем до отправки, а не молча получаем «ничего не найдено».
export const isOutOfLayerRange = (value: number | null, min: number, max: number) =>
  value !== null && (value < min || value > max)

// Сколько знаков после запятой нужно показать: у слоя 6 значения дробные
// (120.5, 8.25), а у слоя 1 целые до 8865.
export const formatBoundary = (value: number) =>
  String(Number(value.toFixed(2)))

// Слой, к которому относится группа: нужен Drawer для подписей атрибутов и
// форме карточки для списка словарей.
export const layerOfGroup = (groups: LayerGroup[], layersById: { [k: number]: Layer }, groupKey: string) => {
  const group = groups.find(g => g.key === groupKey)
  if (!group) return null
  return layersById[group.layerId] ?? null
}
